import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {RowNumbered} from 'src/components/row-numbered';

import {Button} from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {MigrationRelation} from 'src/openapi/admin/models/MigrationRelation';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


type Props = {
    shown: boolean,
    data: EntitlementTransferContext | null,
    onClose: () => void,
    onExport: (context: EntitlementTransferContext) => void,
    busy?: boolean,
};

const MODAL_WIDTH = 913;

export const RolesGrantsResourcesExportResultModal = ({
    shown,
    data,
    onClose,
    onExport,
    busy = false,
}: Props) => {
    const bindings = useMemo(() => data?.bindings ?? [], [data]);
    const [pagination, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 10});

    useEffect(() => {
        if (!shown) {
            setPagination(prev => ({currentPage: 1, pageSize: prev.pageSize}));
        }
    }, [shown]);

    const handlePaginationChange = useCallback((next: Pagination) => {
        setPagination(next);
    }, []);

    const paginatedBindings = useMemo(() => {
        const start = (pagination.currentPage - 1) * pagination.pageSize;
        return bindings.slice(start, start + pagination.pageSize);
    }, [bindings, pagination]);

    if (!data) {
        return null;
    }

    return (
        <Dialog shown={shown} setShown={onClose} wrapperProps={{style: {width: MODAL_WIDTH}}}>
            <DialogHeader onClose={onClose}>
                Export Entitlement structure
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="m">
                    <b>Selected entitlements</b>
                    <Table borderedGrid={true}>
                        <Head>
                            <Cell width={60}><strong>#</strong></Cell>
                            <Cell width={180}>Role</Cell>
                            <Cell width={220}>Resources</Cell>
                            <Cell width={220}>Grant Name</Cell>
                            <Cell width={120}>Grant Status</Cell>
                        </Head>
                        <Body>
                            {paginatedBindings.map((relation: MigrationRelation, index: number) => {
                                const grantStatusLabel = relation.grantStatus
                                    ? relation.grantStatus.charAt(0) + relation.grantStatus.slice(1).toLowerCase()
                                    : '';
                                const absoluteIndex = (pagination.currentPage - 1) * pagination.pageSize + index;

                                return (
                                    <Row key={`${relation.roleExternalId}-${relation.resourceExternalId}-${relation.grantExternalId}-${index}`}>
                                        <Cell width={60}>
                                            <RowNumbered
                                                maxRowNumber={bindings.length}
                                                placeholder={absoluteIndex + 1}
                                            />
                                        </Cell>
                                        <Cell width={180}>
                                            <EllipsisTextHint title={relation.roleName || ''}>
                                                {relation.roleName || ''}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={220}>
                                            <EllipsisTextHint title={relation.resourceName}>
                                                {relation.resourceName}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={220}>
                                            <EllipsisTextHint title={relation.grantName}>
                                                {relation.grantName}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={120}>
                                            <EllipsisTextHint title={grantStatusLabel}>
                                                {grantStatusLabel}
                                            </EllipsisTextHint>
                                        </Cell>
                                    </Row>
                                );
                            })}
                        </Body>
                    </Table>
                    <PaginationStripe
                        currentPage={pagination.currentPage}
                        pageSize={pagination.pageSize}
                        totalCount={bindings.length}
                        onPaginationChange={handlePaginationChange}
                        goToPage={true}
                        sizeSwitcherPosition="LEFT"
                        sizeSwitcherType="ONLY_PAGE_SIZE"
                        sizes={[5, 10]}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group gap="s">
                    <Button
                        variant="Type.PRIMARY"
                        onClick={() => onExport(data)}
                        busy={busy}>
                        Export
                    </Button>
                    <Button
                        variant="Type.SECONDARY"
                        onClick={onClose}
                        disabled={busy}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
